import { router, useForm, usePage } from "@inertiajs/react"
import { ArrowLeftIcon } from "lucide-react"
import { type ReactNode, useEffect } from "react"
import { toast } from "sonner"

import { PrinterIcon } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { FieldError } from "@/components/ui/field"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Textarea } from "@/components/ui/textarea"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { AdminLayout } from "@/layouts/AdminLayout"
import type { PropertySummary } from "@/lib/types"

interface LetterData {
  id: number | null
  greeting_text: string | null
  additional_info: string | null
  delivery_method: "print" | "email" | "download"
  status: string
  custom_sections: {
    property_intro: string
    local_tips: string
    checkout_info: string
    custom: string
  }
}

interface Props {
  property: PropertySummary
  guide: { id: number; title: string }
  letter: LetterData
  urls: { save: string; guide: string; print: string | null }
}

function buildPreview(
  greeting: string,
  propertyIntro: string,
  localTips: string,
  checkoutInfo: string,
  customNotes: string
): string {
  const parts: string[] = []

  if (greeting.trim()) {
    parts.push(greeting.replace(/\{\{guest_name\}\}/g, "{{guest_name}}"))
  }
  if (propertyIntro.trim()) {
    parts.push("--- About Us ---\n" + propertyIntro)
  }
  if (localTips.trim()) {
    parts.push("--- Local Tips ---\n" + localTips)
  }
  if (checkoutInfo.trim()) {
    parts.push("--- Checkout Information ---\n" + checkoutInfo)
  }
  if (customNotes.trim()) {
    parts.push("--- Additional Notes ---\n" + customNotes)
  }

  return parts.join("\n\n") || "(Start typing to see your letter preview…)"
}

export default function WelcomeLetter({ property, guide, letter, urls }: Props) {
  const { props: pageProps } = usePage<{ flash?: { notice?: string } }>()

  const { data, setData, post, patch, processing, errors, transform } = useForm({
    greeting_text:   letter.greeting_text ?? "",
    additional_info: letter.additional_info ?? "",
    delivery_method: letter.delivery_method as "print" | "email" | "download",
    custom_sections: {
      property_intro: letter.custom_sections?.property_intro ?? "",
      local_tips:     letter.custom_sections?.local_tips ?? "",
      checkout_info:  letter.custom_sections?.checkout_info ?? "",
      custom:         letter.custom_sections?.custom ?? "",
    },
  })

  transform((d) => ({ welcome_letter: d }))

  useEffect(() => {
    const notice = pageProps.flash?.notice
    if (notice) toast.success(notice)
  }, [pageProps.flash?.notice])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (letter.id == null) {
      post(urls.save)
    } else {
      patch(urls.save)
    }
  }

  function setSection(key: keyof typeof data.custom_sections, value: string) {
    setData("custom_sections", { ...data.custom_sections, [key]: value })
  }

  const preview = buildPreview(
    data.greeting_text,
    data.custom_sections.property_intro,
    data.custom_sections.local_tips,
    data.custom_sections.checkout_info,
    data.custom_sections.custom
  )

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          onClick={() => router.visit(urls.guide)}
          aria-label="Back to guide"
        >
          <ArrowLeftIcon className="size-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Welcome Letter</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {guide.title} &middot; {property.name}
          </p>
        </div>
      </div>

      {/* 60/40 layout */}
      <div className="flex gap-8">
        {/* Left: editor (60%) */}
        <div className="w-3/5 min-w-0">
          <form onSubmit={handleSubmit} className="flex flex-col gap-6">
            {/* Section 1: Greeting */}
            <div className="grid gap-1.5">
              <Label htmlFor="greeting_text">Greeting</Label>
              <p className="text-xs text-muted-foreground">
                Use <code className="bg-muted px-1 rounded text-xs">{"{{guest_name}}"}</code> as a placeholder for the guest's name.
              </p>
              <Textarea
                id="greeting_text"
                value={data.greeting_text}
                onChange={(e) => setData("greeting_text", e.target.value)}
                rows={3}
                placeholder="Dear {{guest_name}}, welcome to our property…"
              />
              <FieldError>{errors.greeting_text}</FieldError>
            </div>

            {/* Section 2: Property Introduction */}
            <div className="grid gap-1.5">
              <Label htmlFor="property_intro">Property Introduction</Label>
              <Textarea
                id="property_intro"
                value={data.custom_sections.property_intro}
                onChange={(e) => setSection("property_intro", e.target.value)}
                rows={4}
                placeholder="Tell guests about your property…"
              />
            </div>

            {/* Section 3: Local Tips */}
            <div className="grid gap-1.5">
              <Label htmlFor="local_tips">Local Tips</Label>
              <Textarea
                id="local_tips"
                value={data.custom_sections.local_tips}
                onChange={(e) => setSection("local_tips", e.target.value)}
                rows={4}
                placeholder="Share your favourite local recommendations…"
              />
            </div>

            {/* Section 4: Checkout Information */}
            <div className="grid gap-1.5">
              <Label htmlFor="checkout_info">Checkout Information</Label>
              <Textarea
                id="checkout_info"
                value={data.custom_sections.checkout_info}
                onChange={(e) => setSection("checkout_info", e.target.value)}
                rows={3}
                placeholder="Checkout time, key return instructions…"
              />
            </div>

            {/* Section 5: Custom Notes */}
            <div className="grid gap-1.5">
              <Label htmlFor="custom_notes">Custom Notes</Label>
              <Textarea
                id="custom_notes"
                value={data.custom_sections.custom}
                onChange={(e) => setSection("custom", e.target.value)}
                rows={3}
                placeholder="Any additional information for your guests…"
              />
            </div>

            {/* Additional Info */}
            <div className="grid gap-1.5">
              <Label htmlFor="additional_info">Additional Information</Label>
              <Textarea
                id="additional_info"
                value={data.additional_info}
                onChange={(e) => setData("additional_info", e.target.value)}
                rows={2}
                placeholder="Emergency contacts, house rules…"
              />
              <FieldError>{errors.additional_info}</FieldError>
            </div>

            {/* Delivery Method */}
            <div className="grid gap-1.5">
              <Label htmlFor="delivery_method">Delivery Method</Label>
              <TooltipProvider>
                <Select
                  value={data.delivery_method}
                  onValueChange={(v) => setData("delivery_method", v as "print" | "email" | "download")}
                >
                  <SelectTrigger id="delivery_method" className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="print">Print</SelectItem>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <SelectItem value="email" disabled>Email</SelectItem>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>Email delivery coming in Phase 4</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <SelectItem value="download" disabled>Download</SelectItem>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>Email delivery coming in Phase 4</TooltipContent>
                    </Tooltip>
                  </SelectContent>
                </Select>
              </TooltipProvider>
              <FieldError>{errors.delivery_method}</FieldError>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Button type="submit" disabled={processing}>
                {processing ? "Saving…" : "Save letter"}
              </Button>
              {urls.print != null && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => window.open(urls.print!, "_blank")}
                >
                  <PrinterIcon className="size-4 mr-2" />
                  Print / Save as PDF
                </Button>
              )}
              <Button type="button" variant="ghost" onClick={() => router.visit(urls.guide)}>
                Back to Guide
              </Button>
            </div>
          </form>
        </div>

        <Separator orientation="vertical" className="h-auto" />

        {/* Right: preview (40%) */}
        <div className="w-2/5 min-w-0">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                Preview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="whitespace-pre-wrap text-sm leading-relaxed font-sans">
                {preview}
              </pre>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

WelcomeLetter.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
